import { BANK_PARTNERS } from "@/lib/constants";
import { useContent } from "@/lib/content";
import { useReveal } from "@/hooks/use-reveal";

export default function BankPartners({ dark = false }) {
  const r = useReveal();
  const content = useContent();
  // Server-provided banks may not have `logo` filled — auto-derive from domain.
  const serverBanks = content?.banks?.map(b => ({
    ...b,
    logo: b.logo || (b.domain ? `https://www.google.com/s2/favicons?sz=128&domain=${b.domain}` : ""),
  })) || [];
  const partners = serverBanks.length > 0 ? serverBanks : BANK_PARTNERS;
  const bg = dark ? "bg-[#0B1220] text-white" : "bg-[#F8FAFC] text-[#111827]";
  const eyebrow = dark ? "text-[#F59E0B]" : "text-[#0E7490]";
  const cardBg = dark
    ? "bg-white/5 border-white/10 hover:bg-white/10"
    : "bg-white border-[#E2E8F0] hover:shadow-md";
  const nameColor = dark ? "text-white/85" : "text-[#111827]";
  const subColor = dark ? "text-white/50" : "text-[#6B7280]";

  return (
    <section className={`py-16 sm:py-20 ${bg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={r.ref} className={r.className}>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className={`text-sm font-semibold uppercase tracking-widest ${eyebrow}`}>Bank Partners</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold leading-tight">
              FASTag issuance across <span className={dark ? "text-[#F59E0B]" : "text-[#0057B8]"}>all major banks</span>
            </h2>
            <p className={`mt-3 text-base ${subColor}`}>
              We help you obtain, activate and manage FASTags from every leading issuer in India.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {partners.map((b) => (
              <div
                key={b.name}
                data-testid={`bank-partner-${b.short.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group border rounded-2xl p-4 flex flex-col items-center gap-2 text-center transition-shadow duration-200 ${cardBg}`}
                title={b.name}
              >
                <div className="w-12 h-12 rounded-xl bg-white ring-1 ring-black/5 grid place-items-center overflow-hidden">
                  <img
                    src={b.logo}
                    alt={`${b.name} logo`}
                    loading="lazy"
                    decoding="async"
                    className="w-9 h-9 object-contain"
                    onError={(e) => {
                      // Fallback: show bank initial in a colored circle
                      e.currentTarget.replaceWith(Object.assign(document.createElement("div"), {
                        className: "w-9 h-9 rounded-full bg-[#0057B8] text-white font-bold grid place-items-center text-sm",
                        textContent: b.short.charAt(0),
                      }));
                    }}
                  />
                </div>
                <div className={`text-xs font-semibold ${nameColor} leading-tight`}>{b.short}</div>
                <div className={`text-[10px] uppercase tracking-widest ${subColor}`}>FASTag</div>
              </div>
            ))}
          </div>

          <div className={`mt-8 text-center text-xs ${subColor}`}>
            & many more NBFC / bank issuers across India
          </div>
        </div>
      </div>
    </section>
  );
}
